/**
 * useChat Hook
 * 
 * Manages chat state and communication with the backend API.
 * Supports both regular and streaming message responses.
 */

import { useState, useCallback, useRef } from 'react';
import { api, Message, StreamChunk, Chat } from '../services/api';

interface UseChatOptions {
  assistantId?: string;
  onError?: (error: Error) => void;
}

interface UseChatReturn {
  // State
  chat: Chat | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  
  // Actions
  createChat: (assistantId?: string) => Promise<Chat | null>;
  loadChat: (chatId: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  sendMessageStreaming: (content: string) => void;
  cancelStream: () => void;
  clearChat: () => void;
}

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const [chat, setChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const cancelStreamRef = useRef<(() => void) | null>(null);

  // Create a new chat
  const createChat = useCallback(async (assistantId?: string): Promise<Chat | null> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.createChat(assistantId || options.assistantId);
      setChat(response.data);
      setMessages([]);
      
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create chat';
      setError(errorMessage);
      options.onError?.(new Error(errorMessage));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  // Load an existing chat
  const loadChat = useCallback(async (chatId: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const [chatResponse, messagesResponse] = await Promise.all([
        api.getChat(chatId),
        api.getMessages(chatId),
      ]);

      setChat(chatResponse.data);
      setMessages(messagesResponse.data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load chat';
      setError(errorMessage);
      options.onError?.(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  // Send a message (non-streaming)
  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!chat) {
      setError('No active chat');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Optimistically add user message
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        chatId: chat.id,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, tempUserMessage]);

      const response = await api.sendMessage(chat.id, content);

      // Replace temp message and add assistant response
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMessage.id),
        response.data.userMessage,
        response.data.assistantMessage,
      ]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      options.onError?.(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [chat, options]);

  // Send a message with streaming response
  const sendMessageStreaming = useCallback((content: string): void => {
    if (!chat) {
      setError('No active chat');
      return;
    }

    // Cancel any existing stream
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
    }

    setIsStreaming(true);
    setStreamingContent('');
    setError(null);

    // Add user message immediately
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      chatId: chat.id,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    let accumulatedContent = '';

    const cancel = api.streamMessage(
      chat.id,
      content,
      (chunk: StreamChunk) => {
        if (chunk.type === 'content' && chunk.data) {
          accumulatedContent += chunk.data;
          setStreamingContent(accumulatedContent);
        } else if (chunk.type === 'done') {
          // Add the complete assistant message
          const assistantMessage: Message = {
            id: `assistant-${Date.now()}`,
            chatId: chat.id,
            role: 'assistant',
            content: accumulatedContent,
            createdAt: new Date().toISOString(),
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamingContent('');
          setIsStreaming(false);
        } else if (chunk.type === 'error') {
          setError(chunk.error || 'Streaming error');
          setIsStreaming(false);
        }
      },
      (err) => {
        setError(err.message);
        setIsStreaming(false);
        options.onError?.(err);
      }
    );

    cancelStreamRef.current = cancel;
  }, [chat, options]);

  // Cancel current stream
  const cancelStream = useCallback(() => {
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, []);

  // Clear chat state
  const clearChat = useCallback(() => {
    cancelStream();
    setChat(null);
    setMessages([]);
    setError(null);
  }, [cancelStream]);

  return {
    chat,
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    error,
    createChat,
    loadChat,
    sendMessage,
    sendMessageStreaming,
    cancelStream,
    clearChat,
  };
}

export default useChat;
